import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BellRing, CheckCircle2, Camera, X, Info, MapPin } from 'lucide-react';
import { alertIcon } from '../components/layout/alertIcons';
import baseapi, { API_BASE } from '../baseapi';

/**
 * Alerts (14 Oct 2026): raised by the backend (alerts.py) from the live counts, with the camera pictures as evidence,
 * and acknowledged here by a signed-in officer (name from the Keycloak sign-in). One ongoing problem = one alert; it
 * stays open until it is acknowledged (15 Oct 2026).
 * 15 Oct 2026, operators: one table, no tabs, acknowledged in the row; 16 Oct: newest first.
 * /alerts?alert=<id> (the pop-up's "View & acknowledge") opens that alert's camera pictures straight away, with its
 * acknowledgement ready in its row.
 */
interface Alert {
  id: number; key: string; type: string; severity: 'critical' | 'warning' | 'info' | string;
  title: string; message: string | null; place: string | null;
  state: 'open' | 'resolved' | 'suppressed' | string;
  opened_at: number; updated_at: number; resolved_at: number | null;
  needs_ack: boolean; ack_by: string | null; ack_at: number | null; ack_action: string | null; ack_note: string | null;
  check_result: string | null; ai_note: string | null; trial: boolean;
  evidence: { cams?: Record<string, { name: string; verdict: string; people: number | null }> };
  snaps: string[];
}
interface AlertsResponse {
  server_time: number; trial: boolean; actions: string[];
  actions_by_type?: Record<string, string[]>;   // e.g. darshan: scheduled closure first
  counts: { open: number; unacked: number; critical_unacked: number };
  alerts: Alert[];
}

const TYPE_LABEL: Record<string, string> = {
  safety: 'Safety', darshan: 'Darshan', queue: 'Queue', holding: 'Holding area', camera: 'Camera',
  data: 'Count check', forecast: 'Early warning',
};
const hm = (t: number) => new Date(t * 1000).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' });
const mins = (s: number) => {
  const m = Math.max(0, Math.round(s / 60));
  return m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`;
};
const snapUrl = (p: string) => `${API_BASE}${p}`;
// the last picture of each camera of an alert (pictures are named <camera>-<n>.jpg)
const lastFramesOf = (a: Alert) => Object.values(a.snaps.reduce<Record<string, string>>((acc, p) => {
  acc[p.replace(/-\d\.jpg$/, '')] = p; return acc;
}, {}));
// waits for an officer: an open alert, or an information line that asks for it (darshan resumed)
const isWaiting = (a: Alert) => (a.state === 'open' || a.severity === 'info') && a.needs_ack && !a.ack_at;

function sevStyle(sev: string) {
  if (sev === 'critical') return { chip: 'bg-[#BE123C] text-white', label: 'Critical' };
  if (sev === 'warning') return { chip: 'bg-amber-400 text-amber-950', label: 'Warning' };
  return { chip: 'bg-[#4D8DBA] text-white', label: 'Info' };
}

export const AlertsPage: React.FC = () => {
  const [data, setData] = useState<AlertsResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const [ackFor, setAckFor] = useState<number | null>(null);
  const [action, setAction] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [ackError, setAckError] = useState<string | null>(null);
  const [viewer, setViewer] = useState<{ alert: Alert; index: number } | null>(null);
  const [gone, setGone] = useState<Set<number>>(new Set());     // alerts whose pictures are no longer on the server
  const [params, setParams] = useSearchParams();
  const [focus, setFocus] = useState<number | null>(null);       // the row opened from a pop-up, highlighted a while

  const load = useCallback(async () => {
    try {
      const r = await baseapi.get('alerts?state=all&hours=24');
      setData(r.data);
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    load();
    const t = window.setInterval(() => { if (!document.hidden) load(); }, 10000);
    return () => window.clearInterval(t);
  }, [load]);

  const startAck = (a: Alert) => { setAckFor(a.id); setAction(''); setNote(''); setAckError(null); };
  const sendAck = async (a: Alert) => {
    if (!action) { setAckError('Choose what was done'); return; }
    setBusy(true);
    setAckError(null);
    try {
      await baseapi.post(`alerts/${a.id}/ack`, { action, note });
      setAckFor(null);
      await load();
    } catch (e: any) {
      setAckError(e?.response?.status === 401 ? 'Your sign-in has expired. Sign in again and retry.' : 'Could not save. Try again.');
    } finally {
      setBusy(false);
    }
  };

  // opened from a pop-up (?alert=<id>): its pictures at once, its acknowledgement ready, its row in view
  const wanted = Number(params.get('alert')) || null;
  useEffect(() => {
    if (!wanted || !data) return;
    const a = data.alerts.find(x => x.id === wanted);
    setParams({}, { replace: true });
    if (!a) return;
    setFocus(a.id);
    if (isWaiting(a)) startAck(a);
    const frames = lastFramesOf(a);
    if (frames.length) setViewer({ alert: a, index: a.snaps.indexOf(frames[0]) });
    window.setTimeout(() => document.getElementById(`alert-${a.id}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 50);
    window.setTimeout(() => setFocus(f => (f === a.id ? null : f)), 8000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wanted, data]);

  // Escape closes the picture viewer
  useEffect(() => {
    if (!viewer) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setViewer(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewer]);

  const now = data?.server_time ?? Date.now() / 1000;
  const card = 'rounded-xl bg-white dark:bg-[#181b23] border border-neutral-200 dark:border-[#262c39] shadow-xs';
  // newest first, every alert (16 Oct 2026, operators: "sort by time"); waiting rows keep their highlight
  const alerts = [...(data?.alerts ?? [])].sort((a, b) => b.opened_at - a.opened_at);
  const waitingN = alerts.filter(isWaiting).length;
  const th = 'px-3 py-2 text-left text-[11px] font-black uppercase tracking-wider text-neutral-600 dark:text-neutral-400';

  return (
    <div className="space-y-3">
      {/* header */}
      <div className={`${card} p-3.5 flex flex-col lg:flex-row lg:items-center justify-between gap-3`}>
        <div>
          <h1 className="text-lg font-black text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
            <BellRing className="w-5 h-5 text-[#761D20] dark:text-orange-400" /> Alerts · last 24 hours
          </h1>
          <p className="text-sm text-neutral-600 dark:text-neutral-300 mt-0.5">
            Raised from the live camera counts, with the camera pictures as evidence. An alert stays open until an officer acknowledges it.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-2 shrink-0">
          {[
            ['Open', data?.counts.open, 'text-neutral-900 dark:text-neutral-100'],
            ['Waiting for acknowledgement', data?.counts.unacked, 'text-amber-600 dark:text-amber-400'],
            ['Critical, not acknowledged', data?.counts.critical_unacked, 'text-[#BE123C] dark:text-[#F43F5E]'],
          ].map(([label, v, cls]) => (
            <div key={label as string} className="px-3 py-2 rounded-lg bg-neutral-50 dark:bg-[#14161e] border border-neutral-200 dark:border-[#262c39] min-w-[120px]">
              <div className="text-[11px] font-bold text-neutral-600 dark:text-neutral-400 leading-tight">{label}</div>
              <div className={`text-2xl font-black ${cls}`}>{data ? v : '—'}</div>
            </div>
          ))}
        </div>
      </div>

      {data?.trial && (
        <div className="px-3.5 py-2.5 rounded-xl border border-amber-400/60 bg-amber-50 dark:bg-amber-500/10 text-amber-900 dark:text-amber-200 text-sm font-semibold flex items-center gap-2">
          <Info className="w-4 h-4 shrink-0" />
          Trial run: alerts are being collected to tune the rules. Nobody is being paged yet.
        </div>
      )}

      {/* the table */}
      {!data ? (
        <div className={`${card} p-8 text-center text-sm text-neutral-600 dark:text-neutral-400`}>{failed ? 'Alerts not available right now. Retrying…' : 'Loading alerts…'}</div>
      ) : alerts.length === 0 ? (
        <div className={`${card} p-8 text-center text-sm text-neutral-600 dark:text-neutral-400 flex flex-col items-center gap-2`}>
          <CheckCircle2 className="w-8 h-8 text-emerald-500" />
          No alerts in the last 24 hours.
        </div>
      ) : (
        <div className={`${card} overflow-hidden`}>
          <div className="px-3.5 py-2.5 border-b border-neutral-200 dark:border-[#262c39] text-sm font-semibold text-neutral-700 dark:text-neutral-300 flex flex-wrap gap-x-4 gap-y-1">
            <span><b className="text-neutral-900 dark:text-neutral-100">{alerts.length}</b> alerts</span>
            <span><b className="text-amber-600 dark:text-amber-400">{waitingN}</b> waiting for acknowledgement</span>
            <span><b className="text-[#BE123C] dark:text-[#F43F5E]">{alerts.filter(a => a.severity === 'critical').length}</b> critical</span>
            <span><b className="text-emerald-700 dark:text-emerald-400">{alerts.filter(a => a.ack_at).length}</b> acknowledged</span>
            <span><b className="text-neutral-900 dark:text-neutral-100">{alerts.filter(a => a.snaps.length).length}</b> with camera pictures</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[940px] text-sm">
              <thead className="bg-neutral-50 dark:bg-[#14161e] border-b border-neutral-200 dark:border-[#262c39]">
                <tr>
                  <th className={`${th} w-[120px]`}>Time</th>
                  <th className={`${th} w-[120px]`}>Level</th>
                  <th className={th}>Alert</th>
                  <th className={`${th} w-[190px]`}>Evidence</th>
                  <th className={`${th} w-[280px]`}>Acknowledgement</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-[#262c39]">
                {alerts.map(a => {
                  const s = sevStyle(a.severity);
                  const frames = lastFramesOf(a);
                  const open = a.state === 'open';
                  const waiting = isWaiting(a);
                  // critical and not acknowledged for 2+ min: a red bar at the start of the row and a blinking dot
                  const late = waiting && a.severity === 'critical' && now - a.opened_at > 120;
                  const TypeIcon = alertIcon(a.type, a.key, a.severity);
                  return (
                    <tr key={a.id} id={`alert-${a.id}`}
                      className={`align-top transition-shadow ${focus === a.id ? 'ring-2 ring-inset ring-[#761D20]/60 dark:ring-orange-400/70' : ''} ${waiting ? 'bg-amber-50/60 dark:bg-amber-500/[0.06]' : 'hover:bg-neutral-50/70 dark:hover:bg-white/[0.02]'}`}>
                      <td className={`px-3 py-2.5 whitespace-nowrap border-l-4 ${late ? 'border-[#BE123C] dark:border-[#F43F5E]' : waiting ? 'border-amber-400' : 'border-transparent'}`}>
                        <div className="font-bold text-neutral-900 dark:text-neutral-100">{hm(a.opened_at)}</div>
                        <div className="text-xs text-neutral-600 dark:text-neutral-400">
                          {open ? `open · ${mins(now - a.opened_at)}`
                            : a.resolved_at && a.severity !== 'info' ? `to ${hm(a.resolved_at)} · ${mins(a.resolved_at - a.opened_at)}` : ''}
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-black uppercase tracking-wider ${s.chip}`}>{s.label}</span>
                        <div className="mt-1 text-xs font-semibold text-neutral-600 dark:text-neutral-400 flex items-center gap-1">
                          <TypeIcon className="w-3.5 h-3.5 shrink-0" />{TYPE_LABEL[a.type] ?? a.type}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 min-w-0">
                        <div className="font-black text-neutral-900 dark:text-neutral-50 leading-snug">{a.title}</div>
                        {a.message && <div className="text-[13px] text-neutral-700 dark:text-neutral-300 leading-snug mt-0.5">{a.message}</div>}
                        {a.place && (
                          <div className="text-xs text-neutral-600 dark:text-neutral-400 mt-1 flex items-center gap-1">
                            <MapPin className="w-3 h-3 shrink-0" /><span className="truncate">{a.place}</span>
                          </div>
                        )}
                        {a.ai_note && (
                          <div className="text-xs text-neutral-700 dark:text-neutral-300 mt-1"><b>Cameras show:</b> {a.ai_note}</div>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        {frames.length && !gone.has(a.id) ? (
                          <button onClick={() => setViewer({ alert: a, index: a.snaps.indexOf(frames[0]) })} title="Open the pictures"
                            className="flex items-center gap-2 cursor-pointer group text-left">
                            <img src={snapUrl(frames[0])} alt="Camera picture" loading="lazy"
                              onError={() => setGone(g => new Set(g).add(a.id))}
                              className="w-[88px] h-[50px] object-cover rounded-md border border-neutral-200 dark:border-[#262c39] group-hover:ring-2 group-hover:ring-[#761D20]/40 dark:group-hover:ring-orange-400/50" />
                            <span className="text-xs font-semibold text-neutral-700 dark:text-neutral-300 leading-tight">
                              {frames.length} camera{frames.length > 1 ? 's' : ''}<br />{a.snaps.length} picture{a.snaps.length > 1 ? 's' : ''}
                            </span>
                          </button>
                        ) : (
                          <span className="text-xs text-neutral-500 dark:text-neutral-400 inline-flex items-center gap-1">
                            <Camera className="w-3.5 h-3.5" />{frames.length ? 'Pictures no longer kept' : 'No pictures'}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-[13px]">
                        {a.ack_at ? (
                          <div className="text-emerald-800 dark:text-emerald-300">
                            <div className="font-bold flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 shrink-0" />{a.ack_by} · {hm(a.ack_at)}</div>
                            {a.ack_action && <div className="text-neutral-700 dark:text-neutral-300">{a.ack_action}</div>}
                            {a.ack_note && <div className="text-neutral-600 dark:text-neutral-400 italic">“{a.ack_note}”</div>}
                            {open && <div className="text-xs font-semibold text-amber-700 dark:text-amber-400 mt-0.5">Still active</div>}
                          </div>
                        ) : waiting && ackFor === a.id ? (
                          <div className="flex flex-col gap-1.5">
                            <select value={action} onChange={e => setAction(e.target.value)} autoFocus
                              className="px-2 py-1.5 rounded-lg border border-neutral-300 dark:border-[#2b3140] bg-white dark:bg-[#14161e] text-[13px] font-semibold text-neutral-900 dark:text-neutral-100">
                              <option value="">What was done?</option>
                              {(data.actions_by_type?.[a.type] ?? data.actions).map(x => <option key={x} value={x}>{x}</option>)}
                            </select>
                            <input value={note} onChange={e => setNote(e.target.value)} maxLength={500} placeholder="Note (optional)"
                              className="px-2 py-1.5 rounded-lg border border-neutral-300 dark:border-[#2b3140] bg-white dark:bg-[#14161e] text-[13px] text-neutral-900 dark:text-neutral-100" />
                            <div className="flex items-center gap-2">
                              <button disabled={busy} onClick={() => sendAck(a)}
                                className="px-3 py-1.5 rounded-lg bg-[#761D20] dark:bg-orange-500 text-white text-[13px] font-bold cursor-pointer disabled:opacity-50 hover:brightness-110">
                                {busy ? 'Saving…' : 'Confirm'}
                              </button>
                              <button onClick={() => setAckFor(null)} className="px-2.5 py-1.5 rounded-lg text-[13px] font-bold text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-white/5 cursor-pointer">Cancel</button>
                            </div>
                            {ackError && <span className="text-xs font-semibold text-[#BE123C] dark:text-[#F43F5E]">{ackError}</span>}
                          </div>
                        ) : waiting ? (
                          <div className="flex flex-col items-start gap-1.5">
                            <span className={`inline-flex items-center gap-1.5 text-xs font-bold ${late ? 'text-[#BE123C] dark:text-[#F43F5E]' : 'text-amber-800 dark:text-amber-300'}`}>
                              <span className="relative flex h-2 w-2">
                                {late && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#F43F5E] opacity-75" />}
                                <span className={`relative inline-flex rounded-full h-2 w-2 ${late ? 'bg-[#BE123C]' : 'bg-amber-500'}`} />
                              </span>
                              Waiting · {mins(now - a.opened_at)}
                            </span>
                            <button onClick={() => startAck(a)}
                              className="px-3 py-1.5 rounded-lg bg-[#761D20] dark:bg-orange-500 text-white text-[13px] font-bold cursor-pointer hover:brightness-110 shadow-sm">
                              Acknowledge
                            </button>
                          </div>
                        ) : !a.needs_ack ? (
                          <span className="text-neutral-600 dark:text-neutral-400">Information only</span>
                        ) : (
                          <span className="font-semibold text-amber-700 dark:text-amber-400">Cleared without acknowledgement</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* picture viewer: every picture taken for the alert */}
      {viewer && (
        <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4" onClick={() => setViewer(null)}>
          <div className="max-w-5xl w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3 text-white mb-2">
              <div className="min-w-0">
                <div className="text-sm font-bold">{viewer.alert.title} · picture {viewer.index + 1} of {viewer.alert.snaps.length}</div>
                <div className="text-xs text-white/75 truncate">{[viewer.alert.message, viewer.alert.place].filter(Boolean).join(' · ')}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {isWaiting(viewer.alert) && (
                  <button onClick={() => { startAck(viewer.alert); setViewer(null); }}
                    className="px-3 py-1.5 rounded-lg bg-[#761D20] dark:bg-orange-500 text-white text-sm font-bold cursor-pointer hover:brightness-110">
                    Acknowledge
                  </button>
                )}
                <button onClick={() => setViewer(null)} className="p-1 cursor-pointer" aria-label="Close"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <img src={snapUrl(viewer.alert.snaps[viewer.index])} alt="Camera picture" className="w-full rounded-lg" />
            <div className="mt-2 flex gap-1.5 overflow-x-auto">
              {viewer.alert.snaps.map((p, i) => (
                <button key={p} onClick={() => setViewer({ alert: viewer.alert, index: i })} className="shrink-0 cursor-pointer">
                  <img src={snapUrl(p)} alt="" className={`w-[96px] h-[54px] object-cover rounded ${i === viewer.index ? 'ring-2 ring-orange-400' : 'opacity-70'}`} />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlertsPage;
